import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import TraditionTag from "@/components/TraditionTag";
import TaggedOutbound from "@/components/TaggedOutbound";
import { getVisibleAllBooks, primaryStorefront, priceLine } from "@/lib/catalog";
import { trackEvent } from "@/lib/tracking";

// /books — the shop window for everything readable.
//
// It used to render seven graphic novels and stop, which meant a published
// prose title (Koyasan) existed in `products` and nowhere a reader could find
// it. It now renders the whole catalogue, and — the part that matters — each
// card carries its own tagged buy link.
//
// Why the buy link belongs on the index and not only on the detail page: a
// reader who already knows they want the book should not have to load a second
// page to act, and every page between intent and checkout is a page some of
// them leave on. The link is quiet — a flat row under the card, not a button
// competing with the artwork.

function Card({ book, index }) {
  const sf = primaryStorefront(book);
  const price = priceLine(book);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.9,
        delay: Math.min(index * 0.06, 0.32),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="flex flex-col"
    >
      <Link
        to={`/books/${book.slug}`}
        className="block group"
        onClick={() =>
          trackEvent("product_open", {
            title_id: book.slug,
            slug: book.slug,
            surface: "books",
            kind: book.kind,
          })
        }
      >
        <div className="card-shrine rounded-sm overflow-hidden">
          <div className="relative aspect-[2/3] overflow-hidden bg-parchment-deep">
            <img
              src={book.cover_url}
              alt={book.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-slower ease-breath group-hover:scale-[1.04]"
              onError={(e) => {
                // A missing cover must not leave a broken-image glyph in the
                // middle of the shop window.
                e.currentTarget.style.display = "none";
              }}
            />
            {book.is_pending && (
              <div className="absolute top-3 left-3">
                <span className="tradition-tag bg-cream/90 border-saffron-fire/40 text-saffron-fire">
                  Launching
                </span>
              </div>
            )}
            {book.kind === "book" && (
              <div className="absolute top-3 right-3">
                <span className="tradition-tag bg-cream/90">Prose</span>
              </div>
            )}
          </div>

          <div className="p-5 md:p-6">
            <TraditionTag tradition={book.tradition} className="mb-3" />
            <h3 className="font-display text-xl md:text-2xl text-ink leading-tight group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath">
              {book.name}
            </h3>
            {book.anchor?.source && (
              <p className="font-serif italic text-ink-mute text-sm mt-2 leading-snug">
                {book.anchor.source}
              </p>
            )}
          </div>
        </div>
      </Link>

      {/* Tagged buy line. */}
      <div className="mt-3 flex items-center gap-3 flex-wrap min-h-[2rem]">
        {sf ? (
          <TaggedOutbound
            url={sf.url}
            label={sf.label}
            event="buy_click"
            store={sf.id}
            titleId={book.slug}
            slug={book.slug}
            campaign={`books-${book.slug}`}
            className="btn-shop"
            showIcon={false}
            diagnoseNav
          />
        ) : (
          <span className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase">
            In review
          </span>
        )}
        {price && (
          <span className="font-body text-[0.7rem] text-ink-mute leading-tight">
            {price}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function Books() {
  const books = getVisibleAllBooks();
  const novels = books.filter((b) => b.kind !== "book").length;
  const prose = books.length - novels;

  useEffect(() => {
    trackEvent("view_content", {
      title_id: "books",
      slug: "books",
      surface: "books",
      item_count: books.length,
    });
  }, [books.length]);

  return (
    <PageShell title="Books">
      <PageHeader
        eyebrow={`${novels} graphic novels · ${prose} book${prose === 1 ? "" : "s"}`}
        title="Read the scripture as a graphic novel."
        lede="Painterly retellings of stories that have been told for thousands of years. Each book opens with a passage from its source — and ends with the verse that started it."
      />
      <section className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8">
          {books.map((b, i) => (
            <Card key={b.slug} book={b} index={i} />
          ))}
        </div>
      </section>
    </PageShell>
  );
}
